import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import { MessageSquare, Send } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import '@/App.css';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, message } = formData;
    const whatsappMessage = `Ciao! Sono ${name}.\nEmail: ${email}\n\nMessaggio: ${message}`;
    const whatsappUrl = `https://wa.me/390000000000?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/390000000000', '_blank');
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <BrowserRouter>
      <div className="App">
        {/* Sfondo Ipnotico Animato */}
        <div className="hypnotic-background">
          <div className="grid-layer"></div>
          <div className="light-pulse"></div>
          <div className="light-pulse"></div>
          <div className="light-pulse"></div>
          <div className="light-pulse"></div>
          <div className="light-pulse"></div>
          <div className="vertical-pulse"></div>
          <div className="vertical-pulse"></div>
          <div className="vertical-pulse"></div>
        </div>

        {/* Navigation */}
        <Navigation scrollToContact={scrollToContact} />

        {/* Routes */}
        <Routes>
          <Route path="/" element={
            <>
              <HeroSection scrollToContact={scrollToContact} />
              <AboutSection />
              <PortfolioSection />
              <ContactSection 
                formData={formData}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                openWhatsApp={openWhatsApp}
              />
            </>
          } />
          <Route path="/chi-siamo" element={<ChiSiamoPage />} />
        </Routes>

        {/* Footer */}
        <Footer />
      </div>
    </BrowserRouter>
  );
}

function Navigation({ scrollToContact }) {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-blur bg-[#0A0A0B]/80 border-b border-white/10">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_techvision-studio/artifacts/62osyrn8_Gemini_Generated_Image_7wgmzp7wgmzp7wgm.png" 
              alt="PROJECT 01" 
              className="h-10 w-10 object-contain"
              data-testid="logo-nav"
            />
            <span className="brand-heartbeat font-['Outfit'] font-bold text-[#00FFFF] text-xl tracking-tight" data-testid="brand-name">PROJECT 01</span>
          </Link>
          <Link 
            to="/chi-siamo" 
            className="font-['Outfit'] font-medium text-white/80 hover:text-[#00FFFF] transition-colors duration-300"
            data-testid="chi-siamo-link"
          >
            CHI SIAMO
          </Link>
        </div>
        {isHome ? (
          <Button 
            onClick={scrollToContact}
            className="button-elite button-elite-gradient text-black font-['Outfit'] font-semibold transition-all duration-500 px-6"
            data-testid="nav-cta-button"
          >
            CONTATTACI
          </Button>
        ) : (
          <Link to="/">
            <Button 
              className="button-elite button-elite-gradient text-black font-['Outfit'] font-semibold transition-all duration-500 px-6"
              data-testid="nav-home-button"
            >
              HOME
            </Button>
          </Link>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-[#030303] border-t border-white/10 py-12 relative z-10">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_techvision-studio/artifacts/62osyrn8_Gemini_Generated_Image_7wgmzp7wgmzp7wgm.png" 
              alt="PROJECT 01" 
              className="h-12 w-12 object-contain"
              data-testid="logo-footer"
            />
            <div>
              <div className="font-['Outfit'] font-bold text-[#00FFFF] text-xl">PROJECT 01</div>
              <div className="text-white/40 text-sm font-['JetBrains_Mono'] tracking-wider">HIGH-TECH WEB AGENCY</div>
            </div>
          </div>
          <div className="text-white/40 text-sm font-['Satoshi']">
            <a href="mailto:info@project01.tech" className="hover:text-[#00FFFF] transition-colors" data-testid="footer-email">
              info@project01.tech
            </a>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-white/10 text-center text-white/30 text-sm font-['Satoshi']">
          © 2025 PROJECT 01. Soluzioni digitali d'élite.
        </div>
      </div>
    </footer>
  );
}

function HeroSection({ scrollToContact }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section 
      ref={ref}
      className="relative min-h-screen flex items-center justify-center pt-20 px-6 lg:px-12 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#00FFFF]/5 via-transparent to-transparent pointer-events-none"></div>
      
      {/* Logo Background Animato */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img 
          src="https://customer-assets.emergentagent.com/job_techvision-studio/artifacts/62osyrn8_Gemini_Generated_Image_7wgmzp7wgmzp7wgm.png"
          alt=""
          className="logo-background w-[600px] h-[600px] lg:w-[800px] lg:h-[800px] object-contain"
          aria-hidden="true"
        />
      </div>
      
      <div className="max-w-[1440px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center"
        >
          <motion.h1 
            className="gradient-text-flow font-['Outfit'] font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.1] tracking-tighter mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            data-testid="hero-title"
          >
            WEB DESIGN<br />
            D'AVANGUARDIA<br />
            PER IL TUO BUSINESS
          </motion.h1>

          <motion.p 
            className="font-['Satoshi'] text-base sm:text-lg lg:text-xl text-white/85 max-w-3xl mx-auto mb-12 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            data-testid="hero-subtitle"
          >
            Non il solito sito vetrina. Soluzioni digitali d'élite per attività locali che vogliono dominare il mercato.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <Button 
              onClick={scrollToContact}
              className="button-elite button-elite-gradient text-black font-['Outfit'] font-bold text-lg px-10 py-7 transition-all duration-500 transform hover:scale-105"
              data-testid="hero-cta-button"
            >
              INIZIA IL TUO PROGETTO
            </Button>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#030303] to-transparent pointer-events-none"></div>
    </section>
  );
}

function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      ref={ref}
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#030303] relative z-10"
      data-testid="about-section"
    >
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 
            className="gradient-text-flow font-['Outfit'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter mb-8 text-white"
            data-testid="about-title"
          >
            TECNOLOGIA DI
            <br />
            ULTIMA GENERAZIONE
          </h2>
          <p 
            className="font-['Satoshi'] text-base sm:text-lg text-white/80 max-w-4xl mx-auto leading-relaxed"
            data-testid="about-text"
          >
            Sono un web designer specializzato nell'assistere le attività locali. Dimentica i vecchi siti lenti. 
            Utilizzo strumenti di sviluppo di ultima generazione e Intelligenza Artificiale per creare interfacce 
            veloci, moderne e gestibili al 100%. Il risultato? <span className="text-[#00FFFF] font-semibold">Più clienti per te, zero stress tecnico.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function PortfolioSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const projects = [
    {
      id: 'project-beauty',
      title: 'Salone Beauty',
      url: 'https://images.unsplash.com/photo-1604100394559-3cb5bb9a170c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxiZWF1dHklMjBzYWxvbiUyMGRhcmslMjBlbGVnYW50fGVufDB8fHx8MTc3NDY1MjA5M3ww&ixlib=rb-4.1.0&q=85',
      category: 'Beauty & Wellness'
    },
    {
      id: 'project-food',
      title: 'Food & Beverage',
      url: 'https://images.unsplash.com/photo-1740594967618-23cd757b9291?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwcmVzdGF1cmFudCUyMGRhcmslMjBlbGVnYW50fGVufDB8fHx8MTc3NDY1MjA5NHww&ixlib=rb-4.1.0&q=85',
      category: 'Ristorazione'
    },
    {
      id: 'project-retail',
      title: 'Retail Locale',
      url: 'https://images.unsplash.com/photo-1763656070600-b67cd4f2908a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxoaWdoJTIwZW5kJTIwYm91dGlxdWUlMjBkYXJrJTIwZWxlZ2FudHxlbnwwfHx8fDE3NzQ2NTIwOTZ8MA&ixlib=rb-4.1.0&q=85',
      category: 'Retail & Commerce'
    }
  ];

  return (
    <section 
      ref={ref}
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#0A0A0B] relative z-10"
      data-testid="portfolio-section"
    >
      <div className="max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="gradient-text-flow font-['Outfit'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white" data-testid="portfolio-title">
            I NOSTRI PROGETTI
          </h2>
        </motion.div>

        <div className="masonry-grid">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`masonry-item-${index + 1} group overflow-hidden rounded-lg border border-white/10 bg-[#111113] hover:border-[#7351B7]/50 transition-all duration-500 hover:glow-purple-cyan`}
              data-testid={`portfolio-item-${project.id}`}
            >
              <div className="overflow-hidden aspect-[4/3]">
                <img 
                  src={project.url} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <p className="font-['JetBrains_Mono'] text-xs text-[#00FFFF] tracking-[0.2em] uppercase mb-2">{project.category}</p>
                <h3 className="font-['Outfit'] font-semibold text-xl text-white">{project.title}</h3>
              </div>
            </motion.div>
          ))}\n        </div>
      </div>
    </section>
  );
}

function ContactSection({ formData, handleInputChange, handleSubmit, openWhatsApp }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="contact"
      ref={ref}
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#030303] relative z-10"
      data-testid="contact-section"
    >
      <div className="max-w-[800px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="gradient-text-flow font-['Outfit'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white mb-4" data-testid="contact-title">
            INIZIA IL TUO PROGETTO
          </h2>
          <p className="font-['Satoshi'] text-base sm:text-lg text-white/80">Raccontaci la tua idea e ti ricontatteremo al più presto</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-[#111113] border border-white/10 rounded-lg p-8 lg:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="font-['Satoshi'] text-white mb-2 block" data-testid="label-name">
                Nome *
              </Label>
              <Input 
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="bg-[#0A0A0B] border-white/20 text-white focus:border-[#7351B7] focus:ring-1 focus:ring-[#7351B7] font-['Satoshi']"
                placeholder="Il tuo nome"
                data-testid="input-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="font-['Satoshi'] text-white mb-2 block" data-testid="label-email">
                Email *
              </Label>
              <Input 
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="bg-[#0A0A0B] border-white/20 text-white focus:border-[#7351B7] focus:ring-1 focus:ring-[#7351B7] font-['Satoshi']"
                placeholder="tua@email.com"
                data-testid="input-email"
              />
            </div>

            <div>
              <Label htmlFor="message" className="font-['Satoshi'] text-white mb-2 block" data-testid="label-message">
                Parla del tuo progetto *
              </Label>
              <Textarea 
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleInputChange}
                rows={5}
                className="bg-[#0A0A0B] border-white/20 text-white focus:border-[#7351B7] focus:ring-1 focus:ring-[#7351B7] font-['Satoshi'] resize-none"
                placeholder="Descrivi la tua idea, obiettivi e cosa ti aspetti dal sito web..."
                data-testid="textarea-message"
              />
            </div>

            <Button 
              type="submit"
              className="button-elite button-elite-gradient w-full text-black font-['Outfit'] font-bold text-lg py-6 transition-all duration-500 flex items-center justify-center gap-2"
              data-testid="submit-form-button"
            >
              <Send className="w-5 h-5" />
              INVIA RICHIESTA
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-white/10">
            <Button 
              type="button"
              onClick={openWhatsApp}
              className="w-full bg-transparent border-2 border-[#7351B7] text-[#7351B7] font-['Outfit'] font-bold text-lg py-6 hover:bg-[#7351B7]/10 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-all duration-300 flex items-center justify-center gap-3"
              data-testid="whatsapp-button"
            >
              <MessageSquare className="w-6 h-6" />
              PARLIAMONE SU WHATSAPP
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ChiSiamoPage() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section 
      ref={ref}
      className="relative min-h-screen pt-32 pb-24 px-6 lg:px-12"
      data-testid="chi-siamo-page"
    >
      <div className="max-w-[1200px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 
            className="gradient-text-flow font-['Outfit'] font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-8"
            data-testid="chi-siamo-title"
          >
            CHI SIAMO
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          {/* Immagine Designer */}
          <div className="flex justify-center lg:justify-end order-2 lg:order-1">
            <div className="relative">
              <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-[#00FFFF]/20 glow-purple-cyan">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=500&fit=crop"
                  alt="Nicola - Web Designer"
                  className="w-full h-full object-cover"
                  data-testid="nicola-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#00FFFF] text-black font-['Outfit'] font-bold px-6 py-3 rounded-full">
                Nicola
              </div>
            </div>
          </div>

          {/* Testo */}
          <div className="order-1 lg:order-2">
            <p 
              className="font-['Satoshi'] text-lg lg:text-xl text-white/85 leading-relaxed"
              data-testid="chi-siamo-text"
            >
              Ciao, sono <span className="text-[#00FFFF] font-semibold">Nicola</span>, un web designer junior con una passione per il digitale. 
              La mia missione è semplice ma fondamentale: <span className="text-[#7351B7] font-semibold">aiutare le piccole aziende locali a farsi conoscere online</span> e 
              a dominare il loro mercato con soluzioni digitali professionali. 
              <br /><br />
              Credo che ogni attività del territorio meriti una presenza online solida e riconoscibile.
            </p>

            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="flex flex-wrap gap-4">
                <div className="bg-[#111113] border border-[#00FFFF]/20 rounded-lg px-6 py-4">
                  <p className="font-['JetBrains_Mono'] text-xs text-[#00FFFF] tracking-wider uppercase mb-1">Esperienza</p>
                  <p className="font-['Outfit'] text-white font-semibold">Web Design Junior</p>
                </div>
                <div className="bg-[#111113] border border-[#7351B7]/20 rounded-lg px-6 py-4">
                  <p className="font-['JetBrains_Mono'] text-xs text-[#7351B7] tracking-wider uppercase mb-1">Focus</p>
                  <p className="font-['Outfit'] text-white font-semibold">Attività Locali</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default App;